import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CalendarRange, Plus } from "lucide-react";
import type { PayPeriodSummary } from "@crewledger/shared/schemas";
import { EmptyState, PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { formatCurrency } from "@/features/dashboard/utils";
import { BETA_EMPTY_STATES } from "@/lib/beta-empty-states";
import { fetchPayPeriods } from "./api";
import { PayPeriodStatusBadge, payPeriodStatusLabel } from "./components/pay-period-status-badge";
import { usePayPeriodPermissions } from "./use-pay-period-permissions";

export function PayPeriodsListPage() {
  const navigate = useNavigate();
  const { canApprove, canViewPayroll } = usePayPeriodPermissions();
  const [periods, setPeriods] = useState<PayPeriodSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    setLoading(true);
    fetchPayPeriods({ pageSize: 50, status: status || undefined })
      .then((res) => { setPeriods(res.data); })
      .catch((err: unknown) =>
        { setError(err instanceof Error ? err.message : "Failed to load pay periods"); },
      )
      .finally(() => { setLoading(false); });
  }, [status]);

  const showBetaEmpty = periods.length === 0 && !status;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pay Periods"
        description="Review, approve, and lock pay periods for payroll export."
        actions={
          canApprove ? (
            <Link to="/pay-periods/new">
              <Button>
                <Plus className="mr-1.5 h-4 w-4" />
                New period
              </Button>
            </Link>
          ) : undefined
        }
      />

      {showBetaEmpty ? null : (
      <div className="grid gap-3 rounded-xl border bg-card-white p-4 sm:grid-cols-2">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All statuses</option>
          {(["OPEN", "IN_REVIEW", "APPROVED", "LOCKED", "EXPORTED"] as const).map((s) => (
            <option key={s} value={s}>
              {payPeriodStatusLabel(s)}
            </option>
          ))}
        </select>
      </div>
      )}

      {error ? (
        <EmptyState icon={AlertTriangle} title="Could not load pay periods" description={error} />
      ) : loading ? (
        <div className="h-64 animate-pulse rounded-xl border bg-card-white" />
      ) : periods.length === 0 ? (
        <EmptyState
          icon={CalendarRange}
          title={showBetaEmpty ? BETA_EMPTY_STATES.payPeriods.title : "No matching pay periods"}
          description={
            showBetaEmpty
              ? BETA_EMPTY_STATES.payPeriods.description
              : "Try clearing the status filter to see all pay periods."
          }
          actionLabel={showBetaEmpty && canApprove ? BETA_EMPTY_STATES.payPeriods.actionLabel : undefined}
          onAction={showBetaEmpty && canApprove ? () => { navigate("/pay-periods/new"); } : undefined}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card-white">
          <table className="w-full text-sm">
            <thead className="border-b bg-bg-light text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Period</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Workers</th>
                {canViewPayroll ? (
                  <th className="px-4 py-3 font-medium">Gross pay</th>
                ) : null}
                <th className="px-4 py-3 font-medium">Exceptions</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((period) => (
                <tr key={period.id} className="border-b last:border-0 hover:bg-bg-light/50">
                  <td className="px-4 py-3">
                    <Link
                      to={`/pay-periods/${period.id}`}
                      className="font-medium text-navy-deep hover:underline"
                    >
                      {period.label}
                    </Link>
                    <p className="text-xs text-muted-foreground">
                      {period.startDate} → {period.endDate}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <PayPeriodStatusBadge status={period.status} />
                  </td>
                  <td className="px-4 py-3">{period.totalWorkers}</td>
                  {canViewPayroll ? (
                    <td className="px-4 py-3 font-mono">
                      {period.grossPay != null ? formatCurrency(period.grossPay) : "—"}
                    </td>
                  ) : null}
                  <td className="px-4 py-3">
                    {period.openExceptions > 0 ? (
                      <span className="font-medium text-amber-warning">{period.openExceptions}</span>
                    ) : (
                      "0"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
